export interface QuizOption {
  text: string;
  scores: Record<string, number>;
}

export interface QuizQuestion {
  id: number;
  question: string;
  options: QuizOption[];
}

export type GrowthDomain =
  | 'observations'
  | 'feelings'
  | 'needs'
  | 'sovereignty'
  | 'requests'
  | 'naturalization'
  | 'relationship-patterns';

export interface Archetype {
  id: string;
  name: string;
  emoji: string;
  superpower: string;
  description: string;
  growthEdge: string;
  growthDomain: GrowthDomain;
  growthDomainLabel: string;
  growthDomainDescription: string;
}

export const archetypes: Record<string, Archetype> = {
  whisperer: {
    id: 'whisperer',
    name: 'The Whisperer',
    emoji: '🌊',
    superpower: "You hear what people aren't saying.",
    description:
      'You\'re the one friends call at midnight — not because you give the best advice, but because you make people feel heard in a way no one else does. You ask one quiet question and somehow the whole story comes pouring out. People leave conversations with you feeling lighter, even when nothing got "solved."',
    growthEdge:
      "You're so good at holding space for others that people forget to ask how you're doing — and honestly, you've stopped expecting them to. Your journey is learning to turn that same generous listening inward and then outward again, this time with your own voice.",
    growthDomain: 'feelings',
    growthDomainLabel: 'Feelings',
    growthDomainDescription:
      "Your rich inner world stays locked inside — the Feelings domain gives you vocabulary to finally voice what's alive in you.",
  },
  'open-heart': {
    id: 'open-heart',
    name: 'The Open Heart',
    emoji: '💗',
    superpower: 'You feel what others feel — sometimes before they do.',
    description:
      "You walk into a room and immediately know who's faking their smile. You absorb emotions like weather — other people's joy lifts you, their tension knots your stomach. Your emotional radar is uncanny, and it's why people feel so deeply seen around you.",
    growthEdge:
      "Your superpower has a shadow: you can lose yourself in other people's emotional weather. Learning where you end and someone else begins isn't selfish — it's what keeps your gift from burning you out.",
    growthDomain: 'needs',
    growthDomainLabel: 'Needs',
    growthDomainDescription:
      "You carry everyone's emotional weight — the Needs domain teaches you to untangle your own longings from everyone else's.",
  },
  torch: {
    id: 'torch',
    name: 'The Torch',
    emoji: '🔥',
    superpower: "You say what everyone's thinking — with warmth, not weapons.",
    description:
      "When the room is dancing around the elephant, you're the one who names it — but somehow you do it in a way that doesn't send everyone running. You believe clarity is kindness, and you'd rather have a hard honest conversation than a fake easy one.",
    growthEdge:
      "Your directness is refreshing, but sometimes the other person doesn't want your clarity — they want your curiosity. Pausing to listen as boldly as you speak will make your words land even deeper.",
    growthDomain: 'relationship-patterns',
    growthDomainLabel: 'Relationship Patterns',
    growthDomainDescription:
      "Directness can land as criticism (one of Gottman's Four Horsemen) — understanding relational signals helps your truth land as connection, not combat.",
  },
  'first-responder': {
    id: 'first-responder',
    name: 'The First Responder',
    emoji: '🚀',
    superpower: 'Love, for you, is a verb.',
    description:
      'When someone you care about is hurting, you\'re already in the car. You\'re the friend who shows up with soup, fixes the leaky faucet at midnight, and drives two hours because someone sounded "a little off" on the phone. Your loyalty is fierce and your follow-through is unmatched.',
    growthEdge:
      "Sometimes the bravest thing you can do is not fix it. Sitting with someone in their pain — without reaching for the toolbox — is the hardest and most powerful skill you'll learn.",
    growthDomain: 'observations',
    growthDomainLabel: 'Observations',
    growthDomainDescription:
      "You act before you see — the Observations domain teaches you to witness what's actually happening before reaching for the toolbox.",
  },
  diplomat: {
    id: 'diplomat',
    name: 'The Diplomat',
    emoji: '⚖️',
    superpower:
      'You see every side of every story — and you help each person feel fully understood.',
    description:
      "You're the one both friends call after a fight, and somehow you make each one feel fully understood without betraying the other. You have this rare gift of translating between people who seem to be speaking different languages.",
    growthEdge:
      "In all that translating, whose position are you representing? Not just in the big mediations, but in the Tuesday-night disagreements in your own kitchen. It's okay to take your own side — in fact, the people who love you are waiting for it.",
    growthDomain: 'sovereignty',
    growthDomainLabel: 'Sovereignty',
    growthDomainDescription:
      "You represent everyone's position except your own — Sovereignty shows you that your voice belongs in the room too.",
  },
  greenhouse: {
    id: 'greenhouse',
    name: 'The Greenhouse',
    emoji: '🌱',
    superpower:
      'You create the conditions for people to bloom — and then you step back and let them.',
    description:
      "You're patient in a world that isn't. You see who someone could become, and you tend to that possibility — with encouragement, with consistency, with the long game. You don't push. You water, and you wait.",
    growthEdge:
      'You give so freely that asking feels foreign. But reciprocity isn\'t a transaction — it\'s how relationships breathe. Learning to say "here\'s what I want" with the same warmth you say "here\'s what I see in you" changes everything.',
    growthDomain: 'requests',
    growthDomainLabel: 'Requests',
    growthDomainDescription:
      'You give endlessly but rarely ask — the Requests domain teaches you that asking is an act of trust, not weakness.',
  },
  spark: {
    id: 'spark',
    name: 'The Spark',
    emoji: '⚡',
    superpower: 'When something matters to you, everyone in the room knows it.',
    description:
      "Your passion is contagious. When you care about something — a cause, a person, an injustice — you light up, and people can't help but lean in. You're the friend who sends the article, starts the petition, rallies the troops.",
    growthEdge:
      "Fire without aim can burn. Your passion sometimes outpaces your observation — you react to the story in your head before you've fully seen what's in front of you. Slowing down to observe before you ignite will make your fire even more powerful.",
    growthDomain: 'observations',
    growthDomainLabel: 'Observations',
    growthDomainDescription:
      "Your passion outpaces your perception — the Observations domain separates what happened from the story you're telling about it.",
  },
  peacekeeper: {
    id: 'peacekeeper',
    name: 'The Peacekeeper',
    emoji: '🕊️',
    superpower:
      'You create an atmosphere where everyone feels at ease — your care for harmony is a genuine gift.',
    description:
      "You're the emotional thermostat of every room. You sense tension before it surfaces and you smooth it — instinctively, almost invisibly. Harmony isn't just nice for you; it feels necessary. The world feels right when the people around you feel at ease.",
    growthEdge:
      "What happens when your truth is the thing creating the tension? That's where your real courage lives. Discovering that the relationship can hold your honesty — even the inconvenient parts — is the most freeing thing you'll ever learn.",
    growthDomain: 'sovereignty',
    growthDomainLabel: 'Sovereignty',
    growthDomainDescription:
      'You swallow your truth to keep the peace — Sovereignty reveals that authentic expression is peace, not the enemy of it.',
  },
  bedrock: {
    id: 'bedrock',
    name: 'The Bedrock',
    emoji: '🏔️',
    superpower: 'When everything falls apart, people look for you.',
    description:
      "You're the calm in the chaos. Not because you don't feel things — you do, deeply — but because you've learned to steady yourself first so you can be there for others. Your presence alone can de-escalate a room.",
    growthEdge:
      "Strength and invulnerability aren't the same thing. Letting the people who love you see the cracks — not as weakness, but as trust — is how you turn admiration into true intimacy.",
    growthDomain: 'feelings',
    growthDomainLabel: 'Feelings',
    growthDomainDescription:
      "Your steadiness can become a wall — the Feelings domain opens a door to sharing what's actually alive underneath the calm.",
  },
  connector: {
    id: 'connector',
    name: 'The Connector',
    emoji: '🔗',
    superpower: 'You see the invisible threads between people — and you pull them together.',
    description:
      "You're the one who says \"oh, you two HAVE to meet.\" You build tribes without trying. You remember that your colleague's sister is a nurse and your neighbor is looking for medical advice, and somehow by Thursday you've changed two people's lives over a group text.",
    growthEdge:
      "Your wide, beautiful web of connection sometimes means you're everyone's favorite person but nobody's closest. Going deep with a few — truly deep, past the introductions — is where your real fulfillment lives.",
    growthDomain: 'relationship-patterns',
    growthDomainLabel: 'Relationship Patterns',
    growthDomainDescription:
      'You build wide but not always deep — the Relationship Patterns domain teaches you to recognize and respond to bids for deeper connection.',
  },
  alchemist: {
    id: 'alchemist',
    name: 'The Alchemist',
    emoji: '✨',
    superpower: "You trust that honest conflict can deepen connection — and you've seen it happen.",
    description:
      "Where others see a fight, you see an opportunity. Not in a toxic-positivity way — you genuinely believe that the moment of rupture is where the real relationship begins. You've been through enough hard conversations to know that the other side is almost always better.",
    growthEdge:
      'Not everyone is ready to alchemize. Sometimes what looks like a transformation opportunity is actually someone who just wants to be heard — not upgraded. Meeting people in the mess, without trying to transmute it, is your next level.',
    growthDomain: 'naturalization',
    growthDomainLabel: 'Naturalization',
    growthDomainDescription:
      'You have a framework for everything — the Naturalization domain teaches you to drop the process and just be with someone in the raw, unresolved moment.',
  },
  lantern: {
    id: 'lantern',
    name: 'The Lantern',
    emoji: '🏮',
    superpower: 'You reflect back the strengths people forget they have — and you light the way.',
    description:
      'You\'re the friend who says "you\'re being too hard on yourself" at exactly the moment it needs to be said. You see potential where others see failure. People walk away from you believing in themselves a little more — sometimes a lot more.',
    growthEdge:
      "Your gift for perspective can sometimes bypass the feeling. When someone's in pain, they don't always want you to illuminate the lesson — sometimes they just want you to sit in the dark with them for a while first.",
    growthDomain: 'needs',
    growthDomainLabel: 'Needs',
    growthDomainDescription:
      "You illuminate with insight but skip past the feeling — the Needs domain grounds you in what's actually alive for someone before offering perspective.",
  },
};

// ──────────────────────────────────────────────
// 10 Questions — scored across 12 archetypes
// ──────────────────────────────────────────────
//
// Scoring key (0–3):
//   3 = strong signal for this archetype
//   2 = moderate signal
//   1 = slight signal
//   0 = no signal (omitted for readability)
//
// Archetype clusters for reference:
//   Listeners:  whisperer, open-heart, bedrock
//   Speakers:   torch, spark, lantern
//   Relators:   diplomat, greenhouse, connector
//   Doers:      first-responder, peacekeeper, alchemist

export const questions: QuizQuestion[] = [
  // Q1 — Listening style (discriminates Listener cluster + Doers)
  {
    id: 1,
    question: "Your partner comes home upset about their day. What's your first instinct?",
    options: [
      {
        text: 'Listen quietly and give them space to process',
        scores: { whisperer: 3, bedrock: 2, diplomat: 1 },
      },
      {
        text: 'Feel their frustration hit you in the chest immediately',
        scores: { 'open-heart': 3, whisperer: 1, peacekeeper: 1 },
      },
      {
        text: 'Ask them to tell you exactly what happened',
        scores: { torch: 3, lantern: 1, spark: 1 },
      },
      {
        text: 'Start figuring out how to make it better',
        scores: { 'first-responder': 3, greenhouse: 1, alchemist: 1 },
      },
    ],
  },

  // Q2 — Conflict behavior (discriminates Doers + Sovereignty archetypes)
  {
    id: 2,
    question: "In the middle of a heated argument, you're most likely to:",
    options: [
      {
        text: 'Go quiet — you process internally before responding',
        scores: { bedrock: 3, whisperer: 2, peacekeeper: 1 },
      },
      {
        text: "Try to clearly name what you're feeling and why",
        scores: { torch: 3, spark: 1, alchemist: 1 },
      },
      {
        text: 'Look for the compromise that gets everyone back to okay',
        scores: { diplomat: 3, peacekeeper: 2, greenhouse: 1 },
      },
      {
        text: 'Focus on solving the underlying problem, not the emotions',
        scores: { 'first-responder': 3, alchemist: 2, lantern: 1 },
      },
    ],
  },

  // Q3 — How you show love (discriminates Relators + Greenhouse/First Responder)
  {
    id: 3,
    question: 'The people closest to you would say you show love by:',
    options: [
      {
        text: 'Being the one who always truly listens',
        scores: { whisperer: 3, 'open-heart': 2, bedrock: 1 },
      },
      {
        text: 'Showing up — fixing things, helping, being there physically',
        scores: { 'first-responder': 3, greenhouse: 1 },
      },
      {
        text: 'Saying the honest thing no one else will say',
        scores: { torch: 3, spark: 2, lantern: 1 },
      },
      {
        text: 'Connecting them with the right people or opportunities',
        scores: { connector: 3, diplomat: 1, greenhouse: 1 },
      },
    ],
  },

  // Q4 — What drains you (reveals blind spot / growth edge)
  {
    id: 4,
    question: 'After a long week with people, what drains you most?',
    options: [
      {
        text: "Carrying emotions that don't feel like mine",
        scores: { 'open-heart': 3, peacekeeper: 2, whisperer: 1 },
      },
      {
        text: 'Feeling like I held back what I really wanted to say',
        scores: { peacekeeper: 3, whisperer: 2, bedrock: 1 },
      },
      {
        text: "Realizing I helped everyone else but didn't ask for anything",
        scores: { greenhouse: 3, 'first-responder': 2, diplomat: 1 },
      },
      {
        text: 'Having lots of great conversations but none that went really deep',
        scores: { connector: 3, lantern: 2, alchemist: 1 },
      },
    ],
  },

  // Q5 — Role in groups (discriminates across clusters)
  {
    id: 5,
    question: 'In a group of friends, you naturally become:',
    options: [
      {
        text: 'The one who makes sure no one feels left out',
        scores: { connector: 3, greenhouse: 2, peacekeeper: 1 },
      },
      {
        text: 'The one who energizes the room and gets people fired up',
        scores: { spark: 3, torch: 1, connector: 1 },
      },
      {
        text: 'The calm, steady presence people gravitate toward',
        scores: { bedrock: 3, whisperer: 2, diplomat: 1 },
      },
      {
        text: "The one who notices when someone's quietly struggling",
        scores: { 'open-heart': 3, lantern: 2, whisperer: 1 },
      },
    ],
  },

  // Q6 — What you avoid (reveals growth domain)
  {
    id: 6,
    question: 'What do you tend to avoid in relationships?',
    options: [
      {
        text: "Sharing my own feelings before I've fully processed them",
        scores: { whisperer: 3, bedrock: 2, 'open-heart': 1 },
      },
      {
        text: 'Saying something that might upset the other person',
        scores: { peacekeeper: 3, greenhouse: 2, diplomat: 1 },
      },
      {
        text: 'Slowing down — I want to help or fix or respond right away',
        scores: { 'first-responder': 3, spark: 2, torch: 1 },
      },
      {
        text: "Sitting with someone's pain without offering a reframe or silver lining",
        scores: { lantern: 3, alchemist: 2, connector: 1 },
      },
    ],
  },

  // Q7 — Relationship scenario (discriminates Torch/Diplomat/Peacekeeper)
  {
    id: 7,
    question: 'Two friends are in a conflict and both come to you separately. You:',
    options: [
      {
        text: "Help each person see the other's perspective",
        scores: { diplomat: 3, lantern: 1, alchemist: 1 },
      },
      {
        text: "Listen deeply to both, but don't take sides or intervene",
        scores: { whisperer: 2, bedrock: 2, 'open-heart': 2 },
      },
      {
        text: 'Tell each of them honestly what you observe about the situation',
        scores: { torch: 3, spark: 2 },
      },
      {
        text: 'Find a way to get them in the same room and work it out together',
        scores: { alchemist: 3, connector: 2, 'first-responder': 1 },
      },
    ],
  },

  // Q8 — Communication blind spot (directly probes growth edge)
  {
    id: 8,
    question: 'The communication skill you most want to strengthen is:',
    options: [
      {
        text: 'Speaking up about how I feel before it builds up inside',
        scores: { whisperer: 3, bedrock: 2, peacekeeper: 1 },
      },
      {
        text: "Setting boundaries without absorbing everyone's emotions",
        scores: { 'open-heart': 3, greenhouse: 2, peacekeeper: 1 },
      },
      {
        text: 'Listening without planning my response or jumping to insight',
        scores: { torch: 2, lantern: 3, spark: 1 },
      },
      {
        text: 'Being fully present instead of rushing to fix, transform, or connect',
        scores: { 'first-responder': 2, alchemist: 3, connector: 1 },
      },
    ],
  },

  // Q9 — When someone is crying (scenario — discriminates all clusters)
  {
    id: 9,
    question: 'When someone you love is crying, you instinctively:',
    options: [
      {
        text: 'Sit with them in silence, offering your steady presence',
        scores: { bedrock: 3, whisperer: 2 },
      },
      {
        text: 'Feel tears welling up in your own eyes',
        scores: { 'open-heart': 3, greenhouse: 1, peacekeeper: 1 },
      },
      {
        text: "Gently ask what they're going through and what's underneath it",
        scores: { lantern: 2, torch: 2, diplomat: 2 },
      },
      {
        text: 'Get tissues, water, a blanket — anything to help right now',
        scores: { 'first-responder': 3, greenhouse: 2 },
      },
    ],
  },

  // Q10 — After conflict recovery (confirms archetype + growth pattern)
  {
    id: 10,
    question: 'After a big disagreement, you usually:',
    options: [
      {
        text: "Go quiet and reflect until I understand what I'm feeling",
        scores: { whisperer: 2, bedrock: 3, 'open-heart': 1 },
      },
      {
        text: 'Want to talk it through immediately to clear the air',
        scores: { torch: 3, spark: 2, alchemist: 1 },
      },
      {
        text: "Check that the other person is okay — even if I'm still hurting",
        scores: { peacekeeper: 3, greenhouse: 2, 'open-heart': 1 },
      },
      {
        text: 'Look for the lesson or the way this could actually bring you closer',
        scores: { alchemist: 3, lantern: 2, connector: 1 },
      },
    ],
  },
];

/**
 * Calculate the winning archetype from accumulated scores.
 * In case of a tie, the first archetype in definition order wins.
 */
export function calculateArchetype(scores: Record<string, number>): string {
  const archetypeOrder = Object.keys(archetypes);
  let maxScore = 0;
  let result = archetypeOrder[0];

  for (const id of archetypeOrder) {
    const score = scores[id] || 0;
    if (score > maxScore) {
      maxScore = score;
      result = id;
    }
  }

  return result;
}

/**
 * Get a shareable URL slug for an archetype.
 * e.g. "open-heart" → "the-open-heart"
 */
export function getArchetypeSlug(archetypeId: string): string {
  return `the-${archetypeId}`;
}

/**
 * Look up an archetype by its URL slug.
 * e.g. "the-open-heart" → archetypes["open-heart"]
 */
export function getArchetypeBySlug(slug: string): Archetype | undefined {
  const id = slug.replace(/^the-/, '');
  return archetypes[id];
}
